import { PERMISSIONS, type RequestSummary } from "@procuremate/shared";
import { clsx } from "clsx";
import { ChevronRight, MessageSquarePlus } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useHasPermission } from "@/app/require-permission";
import { Button } from "@/components/ui/button";
import type { Language } from "@/i18n";
import { useCreateRequest, useRequests } from "./api";
import { RequestStatusBadge } from "./request-status-badge";

/**
 * Home: the signed-in user's requests and "New request" (WP-2.3). Drafts closed for inactivity are
 * listed apart, under the active ones (WP-3.5, ADR-0024).
 */
export function RequestsPage() {
  const { t, i18n } = useTranslation();
  const canCreate = useHasPermission(PERMISSIONS.REQUESTS_CREATE);
  const requests = useRequests();
  const create = useCreateRequest();
  const closedId = useId();
  const active = requests.data?.filter((r) => r.status !== "ABANDONED") ?? [];
  const closed = requests.data?.filter((r) => r.status === "ABANDONED") ?? [];

  const newButton = canCreate ? (
    <Button disabled={create.isPending} onClick={() => create.mutate(i18n.language as Language)}>
      <MessageSquarePlus aria-hidden="true" className="h-4 w-4" />
      {create.isPending ? t("requests.list.creating") : t("requests.list.new")}
    </Button>
  ) : null;

  return (
    <section className="rounded-lg bg-surface p-4 shadow-card sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-primary">{t("requests.list.title")}</h1>
        {newButton}
      </div>
      {create.isError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {t("requests.list.createError")}
        </p>
      ) : null}

      {requests.isPending ? (
        <p className="mt-6 text-fg-muted">{t("app.loading")}</p>
      ) : requests.isError ? (
        <p role="alert" className="mt-6 text-danger">
          {t("requests.list.loadError")}
        </p>
      ) : requests.data.length === 0 ? (
        <p className="mt-6 max-w-prose text-fg-muted">
          {canCreate ? t("requests.list.empty") : t("requests.list.emptyReadOnly")}
        </p>
      ) : (
        <>
          {active.length > 0 ? <RequestList requests={active} className="mt-6" /> : null}
          {closed.length > 0 ? (
            <section aria-labelledby={closedId} className="mt-8">
              <h2 id={closedId} className="font-heading text-lg font-semibold text-fg">
                {t("requests.list.closedTitle")}
              </h2>
              <p className="mt-1 max-w-prose text-sm text-fg-muted">{t("requests.list.closedHint")}</p>
              <RequestList requests={closed} className="mt-4" />
            </section>
          ) : null}
        </>
      )}
    </section>
  );
}

function RequestList({ requests, className }: { requests: RequestSummary[]; className: string }) {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  return (
    <ul className={clsx("divide-y divide-border rounded-md border border-border", className)}>
      {requests.map((r) => (
        <li key={r.id}>
          <Link to={`/requests/${r.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-bg focus-visible:bg-bg">
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold text-fg">{r.title ?? t("requests.list.untitled")}</span>
                <RequestStatusBadge status={r.status} />
              </span>
              <span className="mt-1 block text-sm text-fg-muted">
                <span className="tabular">{r.reference}</span> ·{" "}
                {t("requests.list.updated", { date: dateFormat.format(new Date(r.updatedAt)) })}
              </span>
            </span>
            <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
